import {
  Button,
  ButtonProps,
  Spinner,
  Stack,
  useColorMode,
  useColorModeValue as mode,
  useTheme,
} from '@chakra-ui/react';
import { transparentize as t } from '@chakra-ui/theme-tools';
import { useCallback, useMemo } from 'react';

import { useSimpleFormContext } from './SimpleForm';
import { SimpleSelect, SimpleSelectProps } from './SimpleSelect';
import { Option } from './SimpleSelect';

// https://github.com/chakra-ui/chakra-ui/blob/c77973070dac3468d701ccb2e7b8ff87c8d430f4/packages/components/theme/src/components/button.ts#L26-L52C3

type Props = SimpleSelectProps & {
  nextValue: Option;
  buttonProps?: ButtonProps;

  isLoading?: boolean;
  wide?: boolean;
};

const useColorProps = (colorScheme: string) => {
  const cs = useCallback((num: number) => `${colorScheme}.${num}`, [colorScheme]);

  const theme = useTheme();
  const { colorMode } = useColorMode();
  const bgMain = mode(cs(50), t(cs(100), 0.25)(theme));
  const colorMain = mode(cs(800), cs(200));
  const borderColorMain = mode(cs(200), cs(500));
  const bgActive = mode(cs(100), t(cs(500), 0.2)(theme));
  const bgFocus = mode(cs(100), t(cs(200), 0.2)(theme));
  const bgHover = mode(cs(100), t(cs(200), 0.25)(theme));
  const borderColorHover = mode(cs(300), cs(400));
  const bgDisabled = mode(cs(50), t(cs(50), 0.12)(theme));
  const props = useMemo(
    () => ({
      bg: bgMain,
      color: colorMain,
      borderColor: borderColorMain,
      borderWidth: 1,
      _active: {
        bg: bgActive,
        borderColor: cs(200),
      },
      _focus: {
        bg: bgFocus,
        borderColor: cs(200),
      },
      _hover: {
        bg: bgHover,
        borderColor: borderColorHover,

        _disabled: {
          bg: bgDisabled,
          borderColor: cs(200),
        },
      },
    }),
    // disabled to include colorMode so that the styles are updated when the colorMode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cs, theme, colorMode],
  );
  return props;
};

export const SimpleQuickSelect = ({
  children,
  colorScheme = 'gray',
  nextValue,
  buttonProps,
  isLoading = false,
  size = 'sm',
  ...rest
}: Props) => {
  const { updateField } = useSimpleFormContext();

  const handleQuickSelect = () => {
    const name = rest.name;
    updateField(name, nextValue.value);
  };

  const selectStyle = useColorProps(colorScheme);

  return (
    <Stack direction="row" align="center" spacing="0" flex={1}>
      <Button
        borderRightRadius={0}
        {...selectStyle}
        size={size}
        onClick={handleQuickSelect}
        {...buttonProps}
        noOfLines={1}
        display="flex"
        flex={1}
        alignItems="center"
        justifyContent="flex-start"
      >
        {nextValue.label}
      </Button>
      <SimpleSelect
        {...rest}
        w="initial"
        size={size}
        selectProps={{
          ...selectStyle,
          borderLeftRadius: '0',
          borderLeftWidth: '0',
          style: { paddingInlineStart: '0.5rem', width: '37px' },
        }}
        icon={isLoading ? <Spinner /> : undefined}
      >
        {children}
      </SimpleSelect>
    </Stack>
  );
};
